import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { palette } from '../../../theme/paletteV2.ts';
import {
  BAR_RADIUS,
  CENTER_LABEL_WIDTH,
  PLACEHOLDER_LEFT_WIDTH,
  PLACEHOLDER_RIGHT_WIDTH,
  PLACEHOLDER_ROWS,
} from '../constants.tsx';

type NoDataOrLoadingStateProps = {
  isLoading: boolean;
  height: number;
};

export const NoDataOrLoadingState = ({ isLoading, height }: NoDataOrLoadingStateProps) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Box
        data-test="pyramid-diagram-loading"
        sx={{
          display: 'grid',
          gridTemplateColumns: `1fr ${CENTER_LABEL_WIDTH}px 1fr`,
          columnGap: 1,
          alignItems: 'center',
          height,
          width: '100%',
          px: 1,
        }}
      >
        {Array.from({ length: PLACEHOLDER_ROWS }, (_, index) => {
          const leftWidth = `${PLACEHOLDER_LEFT_WIDTH.start - index * PLACEHOLDER_LEFT_WIDTH.step}%`;
          const rightWidth = `${PLACEHOLDER_RIGHT_WIDTH.start - index * PLACEHOLDER_RIGHT_WIDTH.step}%`;
          const rowKey = `placeholder-row-${index}`;

          return (
            <Box key={rowKey} sx={{ display: 'contents' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Skeleton
                  animation="wave"
                  variant="rectangular"
                  height={20}
                  width={leftWidth}
                  sx={{ borderRadius: `${BAR_RADIUS}px 0 0 ${BAR_RADIUS}px` }}
                />
              </Box>
              <Skeleton animation="wave" variant="text" width={40} sx={{ mx: 'auto' }} />
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Skeleton
                  animation="wave"
                  variant="rectangular"
                  height={20}
                  width={rightWidth}
                  sx={{ borderRadius: `0 ${BAR_RADIUS}px ${BAR_RADIUS}px 0` }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Box
      data-test="pyramid-diagram-empty"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height,
        width: '100%',
        gap: 0.5,
        textAlign: 'center',
        px: 2,
      }}
    >
      <Typography variant="body1Bold">{t('No data')}</Typography>
      <Typography variant="body1" color={palette.text.secondary}>
        {t('Try adjusting filters or date range to see the results.')}
      </Typography>
    </Box>
  );
};
